// web/src/routes/KidDetail.tsx
// The headline of one kid's journal. Composition:
//   - Back link
//   - Hero band: big KidAvatar (kit-colour ring, squad number), name in
//     headline serif, kicker (age · position · foot), meta line
//   - Action chips (Achievements, Stats, Edit, Delete)
//   - Optional Notes (PullQuote)
//   - Matches list — featured top match (MatchCard variant="featured") +
//     compact MatchCards below, with EmptyState if zero matches

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useKidApi } from '../api/kid';
import { useMatchApi } from '../api/match';
import { ApiError } from '../api/client';
import { Button } from '../components/Button';
import { KidAvatar } from '../components/KidAvatar';
import { MatchCard } from '../components/MatchCard';
import { PullQuote } from '../components/PullQuote';
import { EmptyState } from '../components/EmptyState';
import { Section, Bleed } from '../components/Container';
import { Divider } from '../components/Divider';
import { IconArrowLeft } from '../components/icons';
import { PitchDensityHeatMap } from '../components/Pitch';
import { ageFromDob } from '../lib/age';
import { compressForUpload } from '../lib/image-resize';
import type { Kid, Match, MatchEvent } from '../types';

export function KidDetail() {
  const { kidId } = useParams<{ kidId: string }>();
  const kidApi = useKidApi();
  const matchApi = useMatchApi();
  const navigate = useNavigate();

  const [kid, setKid] = useState<Kid | null>(null);
  const [kidPhotoUrl, setKidPhotoUrl] = useState<string | null>(null);
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [topPhotoUrl, setTopPhotoUrl] = useState<string | null>(null);
  // matchId → signed photo URL for compact match cards in the "Earlier"
  // list. Lazy-fetched after matches load. Only matches with photos hit
  // the presign endpoint, so cost scales with photo'd matches, not total
  // match count.
  const [matchPhotoUrls, setMatchPhotoUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Inline avatar upload (2026-05-19) — when the kid has no profile photo
  // yet, tapping the hero avatar opens the file picker and runs the
  // standard 3-step upload (presign → S3 POST → PATCH photoPath). Saves
  // the detour to KidEdit for the most common path (just-created kid).
  // Same allow-list + size cap as KidPhotoUploader to keep behaviour
  // identical across the two surfaces.
  const photoFileRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const PHOTO_ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
  const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

  const onAvatarTap = () => {
    if (uploadingPhoto || kidPhotoUrl) return;
    photoFileRef.current?.click();
  };

  const onPhotoPicked = async (rawFile: File) => {
    if (!kidId) return;
    setPhotoUploadError(null);
    if (!PHOTO_ALLOWED.has(rawFile.type)) {
      setPhotoUploadError('JPEG, PNG, WebP or HEIC.');
      return;
    }
    if (rawFile.size > PHOTO_MAX_BYTES) {
      setPhotoUploadError(`Under ${Math.round(PHOTO_MAX_BYTES / 1024 / 1024)}MB.`);
      return;
    }
    setUploadingPhoto(true);
    try {
      const file = await compressForUpload(rawFile);
      const presign = await kidApi.presignPhoto(kidId, file.type, file.size);
      const fd = new FormData();
      for (const [k, v] of Object.entries(presign.fields)) fd.append(k, v);
      fd.append('file', file);
      const r = await fetch(presign.uploadUrl, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(`Upload failed (HTTP ${r.status})`);
      await kidApi.update(kidId, { photoPath: presign.key });
      // Refresh — fetch fresh signed URL + bump local kid record so the
      // avatar swaps to the photo without a full reload.
      try {
        const r2 = await kidApi.photoUrl(kidId);
        setKidPhotoUrl(r2.url);
      } catch { /* leave initials briefly; reload will resolve it */ }
      setKid((prev) => (prev ? { ...prev, photoPath: presign.key } : prev));
    } catch (err) {
      const e = err as ApiError;
      setPhotoUploadError(e.message || 'Upload failed.');
    } finally {
      setUploadingPhoto(false);
      if (photoFileRef.current) photoFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!kidId) return;
    let cancelled = false;
    (async () => {
      try {
        const [k, ms] = await Promise.all([kidApi.get(kidId), matchApi.list(kidId)]);
        if (cancelled) return;
        setKid(k);
        const matchList = Array.isArray(ms) ? ms : [];
        setMatches(matchList);

        // Lazy fetch kid profile photo (if set)
        if (k.photoPath) {
          try {
            const r = await kidApi.photoUrl(kidId);
            if (!cancelled) setKidPhotoUrl(r.url);
          } catch { /* fallback to kit colour */ }
        }
        // Lazy fetch photo URLs for every match that has a photo, in parallel.
        // Multi-photo migration: legacy `imagePath` OR new `imagePaths[]`.
        // The photo-url endpoint returns whichever is primary, so we only
        // need to gate the call on "has any photo". Top match (featured
        // card) goes into topPhotoUrl; the rest into the matchPhotoUrls
        // map for the compact cards.
        const live = matchList.filter((m) => !m.deletedAt);
        const top = live[0];
        const withPhoto = live.filter(
          (m) => !!m.imagePath || (Array.isArray(m.imagePaths) && m.imagePaths.length > 0)
        );
        if (withPhoto.length > 0) {
          const results = await Promise.all(
            withPhoto.map(async (m) => {
              try {
                const r = await matchApi.photoUrl(kidId, m.matchId);
                return [m.matchId, r.url] as const;
              } catch {
                return null;
              }
            })
          );
          if (cancelled) return;
          const map: Record<string, string> = {};
          for (const r of results) {
            if (r) map[r[0]] = r[1];
          }
          // Featured card uses its own state slot; everything else lands
          // in the lookup map for the compact list. (Both can co-exist —
          // if the top match is in withPhoto, it gets a URL in both
          // places, harmless.)
          const topUrl = top ? map[top.matchId] : undefined;
          if (topUrl) setTopPhotoUrl(topUrl);
          setMatchPhotoUrls(map);
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as ApiError;
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'Could not load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kidId, kidApi, matchApi]);

  const onDelete = async () => {
    if (!kidId) return;
    setDeleting(true);
    try {
      await kidApi.remove(kidId);
      navigate('/kids');
    } catch (err) {
      const e = err as ApiError;
      setError(e.message || 'Could not delete.');
      setDeleting(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  if (notFound) {
    return (
      <div>
        <h1>Not found</h1>
        <p style={{ color: 'var(--muted)' }}>We couldn't find this kid.</p>
        <Link to="/kids" style={{ color: 'var(--green)' }}>← Back to kids</Link>
      </div>
    );
  }

  if (error || !kid) {
    return (
      <div>
        <h1>Something went wrong</h1>
        <p style={{ color: 'var(--electric-red)' }}>{error || 'Could not load.'}</p>
        <Link to="/kids" style={{ color: 'var(--green)' }}>← Back to kids</Link>
      </div>
    );
  }

  const age = ageFromDob(kid.dob);
  const matchList = matches ?? [];
  const featured = matchList.length > 0 ? matchList[0] : null;
  const rest = matchList.slice(1);
  const fullName = kid.surname ? `${kid.name} ${kid.surname}` : kid.name;

  return (
    <div>
      <Link
        to="/dashboard"
        style={{
          color: 'var(--muted)',
          fontSize: 'var(--small)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 'var(--space-base)',
        }}
      >
        <IconArrowLeft size={16} /> Home
      </Link>

      {/* ── Hero band ───────────────────────────────────────────────── */}
      <Bleed background="var(--surface-3)">
        <div
          style={{
            padding: 'var(--space-xl) var(--space-lg)',
            color: 'var(--ink-on-green)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-lg)',
            flexWrap: 'wrap',
          }}
        >
          {/* Avatar: button when no photo (tap = open file picker → upload),
              static when a photo is set. The hidden file input lives next to
              the avatar so it stays mounted regardless of state. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <input
              ref={photoFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPhotoPicked(f);
              }}
            />
            {kidPhotoUrl ? (
              <KidAvatar
                name={kid.name}
                kitColour={kid.kitColour}
                squadNumber={kid.squadNumber}
                photoUrl={kidPhotoUrl}
                size="lg"
              />
            ) : (
              <button
                type="button"
                onClick={onAvatarTap}
                aria-label={uploadingPhoto ? 'Uploading photo…' : 'Add a photo'}
                disabled={uploadingPhoto}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: uploadingPhoto ? 'progress' : 'pointer',
                  borderRadius: '50%',
                  position: 'relative',
                  opacity: uploadingPhoto ? 0.6 : 1,
                }}
              >
                <KidAvatar
                  name={kid.name}
                  kitColour={kid.kitColour}
                  squadNumber={kid.squadNumber}
                  size="lg"
                />
                {/* Tiny "+" badge in the bottom-right to signal tappability */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    right: 4,
                    bottom: 4,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--gold)',
                    color: 'var(--ink-on-green)',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    border: '2px solid var(--ink-on-green)',
                  }}
                >
                  +
                </span>
              </button>
            )}
            {!kidPhotoUrl ? (
              <span
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--micro)',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: uploadingPhoto ? 'var(--gold-light)' : 'rgba(250,245,236,0.85)',
                  fontWeight: 600,
                }}
              >
                {uploadingPhoto ? 'Uploading…' : 'Tap to add a photo'}
              </span>
            ) : null}
            {photoUploadError ? (
              <span style={{ color: 'var(--gold-light)', fontSize: 'var(--small)', maxWidth: 200, textAlign: 'center' }}>
                {photoUploadError}
              </span>
            ) : null}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--micro)',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--gold-light)',
                marginBottom: 4,
              }}
            >
              {kid.clubName || 'Player profile'}
              {kid.defaultTeamName ? ` · ${kid.defaultTeamName}` : ''}
              {kid.seasonLabel ? ` · ${kid.seasonLabel}` : ''}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--headline)',
                lineHeight: 1.05,
                margin: '0 0 8px',
                color: 'var(--ink-on-green)',
              }}
            >
              {fullName}
            </h1>
            <div
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--small)',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(250,245,236,0.78)',
              }}
            >
              {age !== null ? `Age ${age}` : kid.dob}
              {/* 2026-05-19: Position + kit colour removed from the kid
                  profile UI. Position now lives per-match (see
                  MatchAdd / MatchRecord). preferredFoot retained because
                  it's a stable attribute and parents asked about it. */}
              {kid.preferredFoot ? ` · ${kid.preferredFoot}-footed` : ''}
            </div>
          </div>
        </div>
      </Bleed>

      <div style={{ marginTop: 'var(--space-lg)' }}>
        {/* ── Action chips ───────────────────────────────────────────────
            Two rows: primary (browse — Achievements, Stats) as large rich
            cards; admin (Edit, Delete) as small inline links beneath.
            ─────────────────────────────────────────────────────────────── */}
        <Section spacing="md">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
            }}
          >
            <Link
              to={`/kids/${encodeURIComponent(kid.kidId)}/achievements`}
              style={{ textDecoration: 'none', color: 'inherit' }}
              aria-label="Trophy cabinet"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-base)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--gold-light)',
                  transition: 'transform 180ms var(--ease-editorial), box-shadow 180ms',
                  minHeight: 64,
                }}
              >
                <span style={{ fontSize: '1.75rem', lineHeight: 1 }} aria-hidden>🏆</span>
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--font-masthead)',
                      fontStyle: 'italic',
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      color: 'var(--green)',
                      letterSpacing: '-0.5px',
                      lineHeight: 1,
                    }}
                  >
                    Trophy cabinet
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--micro)',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      marginTop: 4,
                    }}
                  >
                    Achievements unlocked
                  </div>
                </div>
              </div>
            </Link>

            <Link
              to={`/kids/${encodeURIComponent(kid.kidId)}/stats`}
              style={{ textDecoration: 'none', color: 'inherit' }}
              aria-label="Stats"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-base)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface-3)',
                  color: 'var(--ink-on-green)',
                  border: '1px solid var(--green-dark)',
                  minHeight: 64,
                }}
              >
                <span style={{ fontSize: '1.75rem', lineHeight: 1 }} aria-hidden>📊</span>
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--font-masthead)',
                      fontStyle: 'italic',
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      color: 'var(--gold-light)',
                      letterSpacing: '-0.5px',
                      lineHeight: 1,
                    }}
                  >
                    Stats
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--micro)',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(250,245,236,0.7)',
                      marginTop: 4,
                    }}
                  >
                    Season + career
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Admin actions — smaller, quieter, secondary */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-base)',
              marginTop: 'var(--space-md)',
              fontSize: 'var(--small)',
            }}
          >
            <Link
              to={`/kids/${encodeURIComponent(kid.kidId)}/edit`}
              style={{
                color: 'var(--muted)',
                textDecoration: 'underline',
                textDecorationColor: 'var(--divider)',
                textUnderlineOffset: 3,
              }}
            >
              Edit profile
            </Link>
            <Link
              to={`/kids/${encodeURIComponent(kid.kidId)}/print`}
              style={{
                color: 'var(--muted)',
                textDecoration: 'underline',
                textDecorationColor: 'var(--divider)',
                textUnderlineOffset: 3,
              }}
            >
              Print season
            </Link>
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                textDecoration: 'underline',
                textDecorationColor: 'var(--divider)',
                textUnderlineOffset: 3,
                cursor: 'pointer',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                padding: 0,
              }}
            >
              Delete profile
            </button>
          </div>

          {confirmingDelete ? (
            <div
              style={{
                marginTop: 'var(--space-base)',
                padding: 'var(--space-base)',
                background: 'var(--surface-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <p style={{ margin: '0 0 12px 0' }}>
                Delete {kid.name}'s journal? This soft-deletes the record; matches remain
                but are hidden.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  label={deleting ? 'Deleting…' : 'Delete'}
                  onClick={onDelete}
                  loading={deleting}
                  style={{ background: 'var(--electric-red)', color: 'var(--ink-on-green)' }}
                />
                <Button label="Cancel" variant="ghost" onClick={() => setConfirmingDelete(false)} />
              </div>
            </div>
          ) : null}
        </Section>

        {/* ── Notes ──────────────────────────────────────────────────── */}
        {kid.notes ? (
          <Section spacing="lg">
            <PullQuote attribution="Notes about your child">{kid.notes}</PullQuote>
          </Section>
        ) : null}

        {/* ── Matches ────────────────────────────────────────────────── */}
        <Section spacing="lg">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-base)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--title)',
                margin: 0,
                color: 'var(--green)',
              }}
            >
              Matches{' '}
              <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 'var(--body)' }}>
                ({matchList.length})
              </span>
            </h2>
            <Link to={`/kids/${encodeURIComponent(kid.kidId)}/matches/new`} style={{ textDecoration: 'none' }}>
              <Button label="Record a match" />
            </Link>
          </div>

          {matchList.length === 0 ? (
            <EmptyState
              emoji="📓"
              title="The pitch is still"
              body="No matches recorded yet. Use the native app on match day, or add one here."
              cta={
                <Link to={`/kids/${encodeURIComponent(kid.kidId)}/matches/new`} style={{ textDecoration: 'none' }}>
                  <Button label="Record your first match" />
                </Link>
              }
            />
          ) : (
            <>
              {featured ? (
                <div style={{ marginBottom: 'var(--space-base)' }}>
                  <MatchCard
                    match={featured}
                    photoUrl={topPhotoUrl}
                    variant="featured"
                    to={`/kids/${encodeURIComponent(kid.kidId)}/matches/${encodeURIComponent(featured.matchId)}`}
                  />
                </div>
              ) : null}

              {rest.length > 0 ? (
                <>
                  <Divider label={`Earlier · ${rest.length}`} />
                  <div style={{ display: 'grid', gap: 12 }}>
                    {(() => {
                      // Diagnostic 2026-05-19: thumbnail photos aren't showing
                      // on compact cards despite photo-url fetches succeeding.
                      // Log the (matchId → photoUrl) map at render time alongside
                      // each rest-match's imagePath/imagePaths shape, so we can
                      // see exactly what the renderer is working with.
                      // eslint-disable-next-line no-console
                      console.log('[KidDetail] render rest', {
                        matchPhotoUrls,
                        featuredId: featured?.matchId,
                        topPhotoUrlSet: !!topPhotoUrl,
                        rest: rest.map((m) => ({
                          matchId: m.matchId,
                          date: m.date,
                          opposition: m.opposition,
                          imagePath: m.imagePath,
                          imagePathsLen: Array.isArray(m.imagePaths) ? m.imagePaths.length : null,
                          urlInMap: matchPhotoUrls[m.matchId] ?? null,
                        })),
                      });
                      return null;
                    })()}
                    {rest.map((m) => (
                      <MatchCard
                        key={m.matchId}
                        match={m}
                        photoUrl={matchPhotoUrls[m.matchId]}
                        variant="compact"
                        to={`/kids/${encodeURIComponent(kid.kidId)}/matches/${encodeURIComponent(m.matchId)}`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          )}
        </Section>

        {/* ── Season heat-map ──────────────────────────────────────────────
            Broadcast-style density blob across every located event of every
            match — the "where Liam plays" picture (2026-05-19). Renders
            even with zero events (the component shows a friendly nudge);
            we only hide the section entirely when the kid has no matches
            at all so an empty pitch isn't dangling above the fold. */}
        {matchList.length > 0 ? (
          <Section spacing="lg">
            <div
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--micro)',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--gold)',
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Season heat-map
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--title)',
                margin: '0 0 12px',
                color: 'var(--green)',
              }}
            >
              Where {kid.name} plays
            </h2>
            <PitchDensityHeatMap
              events={matchList.reduce<MatchEvent[]>(
                (acc, m) => (Array.isArray(m.events) ? acc.concat(m.events) : acc),
                [],
              )}
            />
          </Section>
        ) : null}
      </div>
    </div>
  );
}
